import React, { useCallback, useMemo } from 'react';

import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { LinkDatePicker } from 'ui-components/components/link-date-picker';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';
import { DateService } from '../../../../../../services/date-service';

import { convertUserRequestedDate } from '../../../../../../fulfillment-plans/convert-user-requested-date';
import {
  useSellerFormattedDate,
  useSellerFormattedTime,
  useIsMidnightInSellerTimeZone,
} from '../../../../../../hooks/use-datetime-formats';
import * as Actions from '../../../../../../store/entities/entities.actions';
import { processEntities } from '../../../../../../store/entities/entities.bulkload';
import Global from '../../../../../../store/global-selectors';

import style from './styles.module.less';

const messages = defineMessages({
  errorNotificationTitle: {
    defaultMessage: 'Error Saving Date',
    id: 'wAPoQz',
    description: 'title on error notification when there is an error persisting the date',
  },
  errorNotificationBody: {
    defaultMessage: 'We encountered an issue saving the date. Please try again.',
    id: 'R1lwFA',
    description: 'description of what went wrong when trying to save a date from the order details section',
  },
  emptyDateMessage: {
    description: 'Placeholder text for a Date Picker input that tells the user the input will set a Date value',
    defaultMessage: 'Set Date',
    id: '0hDh1E',
  },
});

export const EditableFulfillmentPlanDate = ({
  allowPastDates = false,
  apiCall,
  dateValueUtc = '',
  disabled = false,
  emptyStateMessageDescriptor,
  fulfillmentPlanId,
  id,
  shouldUseDateTimePicker = false,
}) => {
  const dispatch = useDispatch();
  const userDateFormat = useSelector(Global.selectUserUserDateFormat);
  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);
  const userTimeFormat = useSelector(Global.selectUserUserTimeFormat);

  const isMaxDate = DateService.isMaxDate(dateValueUtc);
  const effectiveDateValueUtc = isMaxDate ? '' : dateValueUtc;
  const formattedEntityDate = useSellerFormattedDate(effectiveDateValueUtc) ?? '';
  const formattedEntityTime = useSellerFormattedTime(effectiveDateValueUtc, true) ?? undefined;
  const isMidnightInSellerTimeZone = useIsMidnightInSellerTimeZone(effectiveDateValueUtc);
  const buttonText =
    dateValueUtc && !isMidnightInSellerTimeZone ? (
      <div>
        {formattedEntityDate}
        <br />
        {formattedEntityTime}
      </div>
    ) : (
      formattedEntityDate
    );

  // NOTE: a debounce is used because the onChange gets called twice from our calendar date picker
  const onDateChange = useMemo(
    () =>
      debounce(async (dateString) => {
        const dateValue = moment(dateString, userDateFormat);
        const convertedDate = convertUserRequestedDate(dateValue, sellerTimeZone);

        // check if original date is different than changed date because this gets called on blur
        if (formattedEntityDate === dateString) {
          return;
        }

        try {
          const response = await apiCall([fulfillmentPlanId], convertedDate);
          const { data: updatedEntityFulfillmentPlans } = response;
          dispatch(
            Actions.entitiesBulkload(
              processEntities({
                fulfillmentPlans: updatedEntityFulfillmentPlans,
              }),
            ),
          );
        } catch {
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_ERROR,
            title: <FormattedMessage {...messages.errorNotificationTitle} />,
            content: <FormattedMessage {...messages.errorNotificationBody} />,
            duration: NOTIFICATION_DURATIONS.SHORT,
          });
        }
      }, 300),
    [userDateFormat, sellerTimeZone, formattedEntityDate, apiCall, fulfillmentPlanId, dispatch],
  );

  const onIsoDateChange = useCallback(
    async (isoDateUtc) => {
      if (dateValueUtc === isoDateUtc) {
        return;
      }

      try {
        const response = await apiCall([fulfillmentPlanId], isoDateUtc);
        const { data: updatedEntityFulfillmentPlans } = response;
        dispatch(
          Actions.entitiesBulkload(
            processEntities({
              fulfillmentPlans: updatedEntityFulfillmentPlans,
            }),
          ),
        );
      } catch {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorNotificationTitle} />,
          content: <FormattedMessage {...messages.errorNotificationBody} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      }
    },
    [apiCall, dateValueUtc, dispatch, fulfillmentPlanId],
  );

  return (
    <LinkDatePicker
      allowPastDates={allowPastDates}
      buttonText={shouldUseDateTimePicker ? buttonText : undefined}
      dateFormat={userDateFormat}
      disabled={disabled}
      emptyStateMessageDescriptor={emptyStateMessageDescriptor ?? messages.emptyDateMessage}
      id={id}
      linkButtonClassName={style['date-picker-link-button']}
      onChange={shouldUseDateTimePicker ? onIsoDateChange : onDateChange}
      sellerTimeZone={sellerTimeZone}
      shouldUseDateTimePicker={shouldUseDateTimePicker}
      showEmptyState
      timeFormat={userTimeFormat}
      value={shouldUseDateTimePicker ? effectiveDateValueUtc : formattedEntityDate}
    />
  );
};

EditableFulfillmentPlanDate.propTypes = {
  allowPastDates: PropTypes.bool,
  apiCall: PropTypes.func.isRequired,
  dateValueUtc: PropTypes.string,
  disabled: PropTypes.bool,
  emptyStateMessageDescriptor: PropTypes.object,
  fulfillmentPlanId: PropTypes.string.isRequired,
  id: PropTypes.string,
  shouldUseDateTimePicker: PropTypes.bool,
};
